import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { MapPin, Phone, Mail, Clock, MessageCircle, CheckCircle, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from '../hooks/use-toast';
import './ContactSection.css';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos requeridos.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Send to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Error al enviar el mensaje');
      }

      const data = await response.json();

      // Scroll to top smoothly
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      // Show success modal after a brief delay to allow scroll
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 500);

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
      });
    } catch (error) {
      console.error('Error sending form:', error);
      toast({
        title: 'Error',
        description: 'Hubo un problema al enviar el mensaje. Por favor intenta nuevamente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <section id="contacto" className="contact-section">
      <div className="contact-container">
        <div className="section-header">
          <h2 className="section-title">Contáctanos</h2>
          <p className="section-description">
            Estamos aquí para responder tus preguntas y acompañarte en el proceso.
            <br />
            Completa el formulario y nos pondremos en contacto a la brevedad.
          </p>
        </div>

        <div className="contact-grid">
          <Card className="contact-form-card">
            <h3 className="form-title">Envíanos un Mensaje</h3>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Nombre Completo <span className="required">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Tu nombre completo"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Teléfono <span className="required">*</span>
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="+56 9 1234 5678"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Correo Electrónico <span className="required">*</span>
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="tu@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Mensaje <span className="required">*</span>
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Cuéntanos cómo podemos ayudarte..."
                  rows={5}
                />
              </div>

              <Button type="submit" className="submit-button">
                Enviar Mensaje
              </Button>
            </form>
          </Card>

          <div className="contact-info">
            <Card className="info-card">
              <div className="info-item">
                <div className="info-icon-wrapper">
                  <MapPin className="info-icon" />
                </div>
                <div className="info-content">
                  <h4 className="info-title">Dirección</h4>
                  <p className="info-text">Antonio Jacobo Vial 1788</p>
                  <p className="info-text">Independencia, Santiago de Chile</p>
                </div>
              </div>
            </Card>

            <Card className="info-card">
              <div className="info-item">
                <div className="info-icon-wrapper">
                  <Phone className="info-icon" />
                </div>
                <div className="info-content">
                  <h4 className="info-title">Teléfono</h4>
                  <p className="info-text">+56 9 4945 5611</p>
                  <a 
                    href="https://wa.me/56949455611" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="whatsapp-link"
                  >
                    <MessageCircle size={18} />
                    <span>Enviar WhatsApp</span>
                  </a>
                </div>
              </div>
            </Card>

            <Card className="info-card">
              <div className="info-item">
                <div className="info-icon-wrapper">
                  <Mail className="info-icon" />
                </div>
                <div className="info-content">
                  <h4 className="info-title">Email</h4>
                  <p className="info-text">contacto@centroterapeuticocrecerjuntos.cl</p>
                </div>
              </div>
            </Card>

            <Card className="info-card">
              <div className="info-item">
                <div className="info-icon-wrapper">
                  <Clock className="info-icon" />
                </div>
                <div className="info-content">
                  <h4 className="info-title">Horario</h4>
                  <p className="info-text">Lunes a Viernes: 10:00 - 19:00</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="success-modal">
          <button
            onClick={() => setShowSuccessModal(false)}
            className="modal-close-button"
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
          <div className="modal-content-wrapper">
            <div className="success-icon-wrapper">
              <CheckCircle size={64} className="success-icon" />
            </div>
            <DialogHeader>
              <DialogTitle className="success-title">
                ¡Mensaje Enviado con Éxito!
              </DialogTitle>
            </DialogHeader>
            <p className="success-message">
              Hemos recibido tu mensaje y nos pondremos en contacto contigo a la brevedad.
            </p>
            <p className="success-submessage">
              Gracias por contactar al Centro Terapéutico Crecer Juntos.
            </p>
            <Button
              onClick={() => setShowSuccessModal(false)}
              className="success-close-button"
            >
              Aceptar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ContactSection;